import { useEffect, useMemo, useState, useCallback } from "react";
import { api, brl, fmtDate } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Plus, Eye, PencilLine, RefreshCw, Search, Receipt, Ban, AlertTriangle,
  CheckCircle2, RotateCw, FileText, UploadCloud, Download, Trash2, FileSpreadsheet,
  Sparkles, Loader2,
} from "lucide-react";
import { toast } from "sonner";
import { FornecedorPicker as _SharedFornecedorPicker } from "@/components/FornecedorPicker";

/* F7.4 — Picker extraído para /components/FornecedorPicker.jsx (compartilhado com F5/F6).
   Wrapper preserva testids originais (`cp-fornecedor-picker`, `input-cp-fornecedor`, etc.). */
function FornecedorPicker(props) {
  return (
    <_SharedFornecedorPicker
      {...props}
      pickerTestId="cp-fornecedor-picker"
      inputTestId="input-cp-fornecedor"
      labelText="Fornecedor *"
    />
  );
}

// Manter o componente antigo local desabilitado (não usado; mantido só para não quebrar diffs futuros).
// eslint-disable-next-line no-unused-vars
function _LegacyFornecedorPickerRemoved({ value, valueTexto, onSelect, onClear, onChangeTexto }) {
  const formatarCPFCurto = (digits) => {
    const d = (digits || "").padEnd(11, "").slice(0, 11);
    return d.replace(/^(\d{3})(\d{3})(\d{3})(\d{2}).*/, "$1.$2.$3-$4");
  };
  const formatarCNPJCurto = (digits) => {
    const d = (digits || "").padEnd(14, "").slice(0, 14);
    return d.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2}).*/, "$1.$2.$3/$4-$5");
  };
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedNome, setSelectedNome] = useState("");
  const [selectedTipo, setSelectedTipo] = useState("");
  const [selectedDoc, setSelectedDoc] = useState("");

  useEffect(() => {
    if (!value) {
      setSelectedNome(""); setSelectedTipo(""); setSelectedDoc("");
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/fornecedores/${value}`);
        if (cancelled) return;
        setSelectedNome(data.razao_social_nome || "");
        setSelectedTipo(data.tipo_pessoa || "");
        setSelectedDoc(data.documento_normalizado || "");
      } catch {
        // fornecedor pode ter sido desativado; mantém texto legado
      }
    })();
    return () => { cancelled = true; };
  }, [value]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const params = { ativo: true, limit: 20 };
        const q = query.trim();
        if (q) {
          // Se parece com número (só dígitos com pontuação de doc), filtra por documento
          const digits = q.replace(/\D+/g, "");
          if (digits && digits.length >= 3) {
            params.documento = digits;
          } else {
            params.nome = q;
          }
        }
        const { data } = await api.get("/fornecedores", { params });
        if (!cancelled) setResults(Array.isArray(data) ? data : []);
      } catch {
        if (!cancelled) setResults([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 250);
    return () => { cancelled = true; clearTimeout(t); };
  }, [query, open]);

  const docFormatado = (f) => {
    if (!f.documento_normalizado) return "";
    return f.tipo_pessoa === "PF"
      ? formatarCPFCurto(f.documento_normalizado)
      : formatarCNPJCurto(f.documento_normalizado);
  };

  const escolher = (f) => {
    onSelect(f);
    setOpen(false);
    setQuery("");
  };

  const limpar = () => {
    onClear();
    setSelectedNome(""); setSelectedTipo(""); setSelectedDoc("");
  };

  return (
    <div data-testid="cp-fornecedor-picker">
      <div className="flex items-center justify-between mb-1">
        <Label>Fornecedor *</Label>
        {value && (
          <button type="button" onClick={limpar}
            data-testid="btn-cp-fornecedor-limpar"
            className="text-[11px] text-[#D4A652] hover:underline">
            Limpar vínculo
          </button>
        )}
      </div>
      {value ? (
        <div data-testid="cp-fornecedor-selecionado"
          className="border border-[#083A6B]/30 bg-[#EAF0EA]/50 rounded-md p-2.5 flex items-center justify-between gap-2">
          <div className="min-w-0">
            <div className="text-sm text-stone-900 font-medium truncate">
              {selectedNome || valueTexto || "(carregando…)"}
            </div>
            <div className="text-[11px] text-stone-600">
              {selectedTipo && <span className="mr-2">{selectedTipo}</span>}
              {selectedDoc && <span className="font-mono">{
                selectedTipo === "PF" ? formatarCPFCurto(selectedDoc) : formatarCNPJCurto(selectedDoc)
              }</span>}
            </div>
          </div>
          <span className="text-[10px] uppercase tracking-widest text-[#083A6B] font-semibold shrink-0">
            Vinculado
          </span>
        </div>
      ) : (
        <>
          <div className="relative">
            <Input
              data-testid="input-cp-fornecedor"
              value={valueTexto}
              placeholder="Digite para buscar (nome, fantasia ou documento)"
              onFocus={() => setOpen(true)}
              onChange={(e) => {
                onChangeTexto(e.target.value);
                setQuery(e.target.value);
                setOpen(true);
              }}
              required
            />
            {open && (query.length > 0 || results.length > 0) && (
              <div data-testid="cp-fornecedor-dropdown"
                className="absolute z-50 top-full left-0 right-0 mt-1 bg-white border border-stone-200 rounded-md shadow-lg max-h-64 overflow-auto">
                {loading ? (
                  <div className="p-3 text-xs text-stone-500">Buscando…</div>
                ) : results.length === 0 ? (
                  <div className="p-3 text-xs text-stone-500">
                    Nenhum fornecedor ativo encontrado.
                    {" "}Você pode continuar digitando o texto livre.
                  </div>
                ) : (
                  <ul>
                    {results.map((f) => (
                      <li key={f.id}>
                        <button type="button"
                          data-testid={`opt-cp-fornecedor-${f.id}`}
                          onClick={() => escolher(f)}
                          className="w-full text-left px-3 py-2 hover:bg-[#EAF0EA] focus:bg-[#EAF0EA] focus:outline-none">
                          <div className="text-sm text-stone-900 flex items-center gap-2">
                            <span className="text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded bg-stone-100 text-stone-600">
                              {f.tipo_pessoa}
                            </span>
                            <span className="truncate">{f.razao_social_nome}</span>
                          </div>
                          {(f.nome_fantasia || f.documento_normalizado) && (
                            <div className="text-[11px] text-stone-500 mt-0.5">
                              {f.nome_fantasia && <span className="mr-2">{f.nome_fantasia}</span>}
                              {f.documento_normalizado && (
                                <span className="font-mono">{docFormatado(f)}</span>
                              )}
                            </div>
                          )}
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>
          {open && (
            <button type="button" onClick={() => setOpen(false)}
              className="fixed inset-0 z-40 cursor-default"
              tabIndex={-1} aria-hidden="true" />
          )}
          <p className="text-[11px] text-stone-500 mt-1">
            Selecione um fornecedor cadastrado para vincular. Se não encontrar, o texto livre
            fica registrado e você pode <a href="/admin/financeiro/fornecedores" className="underline text-[#083A6B]">cadastrar em Fornecedores</a>.
          </p>
        </>
      )}
    </div>
  );
}


// Espelha CONTA_STATUSES + status derivado "vencida"
const STATUSES = [
  { key: "pendente", label: "Pendente" },
  { key: "programada", label: "Programada" },
  { key: "paga", label: "Paga" },
  { key: "vencida", label: "Vencida" },
  { key: "cancelada", label: "Cancelada" },
];
const STATUS_LABEL = Object.fromEntries(STATUSES.map((s) => [s.key, s.label]));

// Espelha CATEGORIAS_VALIDAS (16 opções, sem alteração)
const CATEGORIAS = [
  { key: "agua", label: "Água" },
  { key: "luz", label: "Luz" },
  { key: "manutencao", label: "Manutenção" },
  { key: "internet_telefone", label: "Internet / Telefone" },
  { key: "combustivel", label: "Combustível" },
  { key: "uso_consumo", label: "Uso e Consumo" },
  { key: "limpeza", label: "Limpeza" },
  { key: "servicos_prestados", label: "Serviços Prestados" },
  { key: "funcionarios", label: "Funcionários" },
  { key: "inss", label: "INSS" },
  { key: "fgts", label: "FGTS" },
  { key: "impostos", label: "Impostos" },
  { key: "taxas", label: "Taxas" },
  { key: "aluguel", label: "Aluguel" },
  { key: "imobilizado", label: "Imobilizado" },
  { key: "outros", label: "Outros" },
];
const CAT_LABEL = Object.fromEntries(CATEGORIAS.map((c) => [c.key, c.label]));

// Espelha FORMAS_PAGAMENTO
const FORMAS = [
  { key: "pix", label: "PIX" },
  { key: "cartao", label: "Cartão" },
  { key: "boleto", label: "Boleto" },
  { key: "dinheiro", label: "Dinheiro" },
  { key: "transferencia", label: "Transferência" },
];
const FORMA_LABEL = Object.fromEntries(FORMAS.map((f) => [f.key, f.label]));

// F13.5.2 — Regras de comprovante (espelhando o contrato do backend)
const CP_COMPROVANTE_MAX_BYTES = 10 * 1024 * 1024; // 10 MB
const CP_COMPROVANTE_MIME_ALLOWED = ["application/pdf", "image/jpeg", "image/jpg", "image/png"];
const CP_COMPROVANTE_EXT_ALLOWED = [".pdf", ".jpg", ".jpeg", ".png"];

// F13.5.3 — Regras de Import CSV (espelhando o contrato do backend)
const CP_CSV_MAX_BYTES = 2 * 1024 * 1024; // 2 MB

function cpFmtBytes(n) {
  const v = Number(n || 0);
  if (!v || v < 0) return "—";
  if (v < 1024) return `${v} B`;
  if (v < 1024 * 1024) return `${(v / 1024).toFixed(1)} KB`;
  return `${(v / 1024 / 1024).toFixed(2)} MB`;
}

function cpFmtDataHoraBR(iso) {
  if (!iso) return "—";
  try {
    const dt = new Date(iso);
    if (Number.isNaN(dt.getTime())) return "—";
    const dd = String(dt.getDate()).padStart(2, "0");
    const mm = String(dt.getMonth() + 1).padStart(2, "0");
    const yy = dt.getFullYear();
    const hh = String(dt.getHours()).padStart(2, "0");
    const mi = String(dt.getMinutes()).padStart(2, "0");
    return `${dd}/${mm}/${yy} ${hh}:${mi}`;
  } catch { return "—"; }
}

function cpParseFilenameFromDisposition(disposition) {
  if (!disposition) return null;
  const utf8Match = /filename\*=UTF-8''([^;]+)/i.exec(disposition);
  if (utf8Match?.[1]) {
    try { return decodeURIComponent(utf8Match[1].trim().replace(/^"|"$/g, "")); }
    catch { /* ignore */ }
  }
  const simple = /filename="?([^";]+)"?/i.exec(disposition);
  return simple?.[1] ? simple[1].trim() : null;
}

function cpSaveBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename || "arquivo";
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 500);
}

function today() { return new Date().toISOString().slice(0, 10); }
function firstDayOfMonth() {
  const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`;
}
function lastDayOfMonth() {
  const d = new Date(); const l = new Date(d.getFullYear(), d.getMonth() + 1, 0);
  return `${l.getFullYear()}-${String(l.getMonth() + 1).padStart(2, "0")}-${String(l.getDate()).padStart(2, "0")}`;
}

const emptyForm = () => ({
  fornecedor: "",
  fornecedor_id: "",
  descricao: "",
  categoria: "outros",
  valor: "",
  data_vencimento: today(),
  data_programada_pagamento: "",
  forma_pagamento: "",
  observacao: "",
  competencia: "",
  pousada_id: "",
  is_recorrente: false,
  recorrencia_dia_vencimento: "",
  recorrencia_meses_restantes: "",
});

function StatusBadge({ status }) {
  const s = status || "pendente";
  const cls = {
    pendente: "bg-stone-100 text-stone-700 border border-stone-300",
    programada: "bg-[#EDE9FE] text-[#5B21B6] border border-[#5B21B6]/25",
    paga: "bg-[#EAF0EA] text-[#083A6B] border border-[#083A6B]/20",
    vencida: "bg-[#F5E4DD] text-[#7c3a20] border border-[#D4A652]/25",
    cancelada: "bg-stone-100 text-stone-400 border border-stone-300 line-through",
  }[s] || "bg-stone-100 text-stone-700 border border-stone-300";
  return (
    <span
      data-testid={`cp-status-${s}`}
      className={`inline-block px-2 py-0.5 rounded-full text-[10px] uppercase tracking-widest font-semibold ${cls}`}
    >
      {STATUS_LABEL[s] || s}
    </span>
  );
}

function KpiCard({ label, count, valor, tone = "neutral", testid }) {
  const cls = {
    danger: "bg-[#F5E4DD] text-[#7c3a20] border-[#D4A652]/25",
    warn: "bg-[#FFF4D6] text-[#8B6A1F] border-[#8B6A1F]/25",
    ok: "bg-[#EAF0EA] text-[#083A6B] border-[#083A6B]/20",
    info: "bg-[#EDE9FE] text-[#5B21B6] border-[#5B21B6]/25",
    neutral: "bg-stone-50 text-stone-800 border-stone-200",
  }[tone];
  return (
    <div data-testid={testid} className={`border rounded-lg px-4 py-3 ${cls}`}>
      <div className="text-[11px] uppercase tracking-widest opacity-70">{label}</div>
      <div className="font-serif text-xl tabular-nums">{brl(valor)}</div>
      <div className="text-[11px] opacity-80">{count} {count === 1 ? "conta" : "contas"}</div>
    </div>
  );
}

export default function ContasPagar() {
  const [items, setItems] = useState([]);
  const [resumo, setResumo] = useState(null);
  const [pousadas, setPousadas] = useState([]);
  const [me, setMe] = useState(null);

  const [loading, setLoading] = useState(true);
  const [erroCarga, setErroCarga] = useState(null);

  const [busca, setBusca] = useState("");
  const [filtros, setFiltros] = useState({
    data_inicio: firstDayOfMonth(),
    data_fim: lastDayOfMonth(),
    status: "all",
    categoria: "all",
    pousada_id: "all",
  });

  const [formOpen, setFormOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);

  // F-FIX.CP.9 — Importar conta via Resende IA (OCR preview).
  const [ocrOpen, setOcrOpen] = useState(false);
  const [ocrStage, setOcrStage] = useState("upload"); // "upload" | "processing" | "preview"
  const [ocrFile, setOcrFile] = useState(null);
  const [ocrPreview, setOcrPreview] = useState(null); // dados retornados pela IA (editáveis)
  const [ocrSaving, setOcrSaving] = useState(false);

  const [detalheAlvo, setDetalheAlvo] = useState(null);
  const [detalheOpen, setDetalheOpen] = useState(false);
  const [detalheLoading, setDetalheLoading] = useState(false);

  const [pagarAlvo, setPagarAlvo] = useState(null);
  const [pagarForm, setPagarForm] = useState({ data_pagamento: today(), forma_pagamento: "" });
  const [pagando, setPagando] = useState(false);

  const [cancelarAlvo, setCancelarAlvo] = useState(null);
  const [cancelarMotivo, setCancelarMotivo] = useState("");
  const [cancelando, setCancelando] = useState(false);

  // F13.5.2 — Comprovante (upload/download/remoção)
  const [cpUploadingComp, setCpUploadingComp] = useState(false);
  const [cpDownloadingComp, setCpDownloadingComp] = useState(false);
  const [cpRemovingComp, setCpRemovingComp] = useState(false);
  const [cpConfirmRemoveOpen, setCpConfirmRemoveOpen] = useState(false);

  // F13.5.3 — Import CSV
  const [csvOpen, setCsvOpen] = useState(false);
  const [csvFile, setCsvFile] = useState(null);
  const [csvImporting, setCsvImporting] = useState(false);
  const [csvDownloadingTemplate, setCsvDownloadingTemplate] = useState(false);
  const [csvResult, setCsvResult] = useState(null);
  const [csvErro, setCsvErro] = useState("");

  // F13.5.5 — Export CSV
  const [csvExporting, setCsvExporting] = useState(false);

  // F13.5.6 — Auditoria CP (somente leitura)
  const [cpAuditItems, setCpAuditItems] = useState([]);
  const [cpAuditLoading, setCpAuditLoading] = useState(false);
  const [cpAuditErro, setCpAuditErro] = useState("");

  const podeEscrever = !!(me && (me.is_admin || me.is_finance));

  // F13.5.2 — Comprovante: handlers unificados de erro
  const _cpCompUploadError = (err) => {
    const s = err?.response?.status;
    const raw = err?.response?.data?.detail;
    const detail = typeof raw === "string" ? raw : (raw ? JSON.stringify(raw) : "");
    if (s === 400) toast.error(detail || "Arquivo inválido");
    else if (s === 401) toast.error("Sessão expirada. Faça login novamente.");
    else if (s === 403) toast.error("Sem permissão para anexar comprovante");
    else if (s === 404) toast.error("Conta não encontrada");
    else if (s === 413) toast.error(`Arquivo maior que ${CP_COMPROVANTE_MAX_BYTES / 1024 / 1024} MB`);
    else if (s === 415) toast.error(detail || "Tipo de arquivo não permitido");
    else if (s === 502 || s === 503) toast.error("Serviço de armazenamento indisponível — tente novamente");
    else toast.error("Erro inesperado ao enviar comprovante");
  };

  const _cpCompDownloadError = (err) => {
    const s = err?.response?.status;
    if (s === 404) toast.error("Comprovante não anexado");
    else if (s === 401) toast.error("Sessão expirada. Faça login novamente.");
    else if (s === 403) toast.error("Sem permissão para baixar comprovante");
    else if (s === 502 || s === 503) toast.error("Serviço de armazenamento indisponível");
    else toast.error("Erro inesperado ao baixar comprovante");
  };

  const _cpCompRemoveError = (err) => {
    const s = err?.response?.status;
    if (s === 401) toast.error("Sessão expirada. Faça login novamente.");
    else if (s === 403) toast.error("Sem permissão para remover comprovante");
    else if (s === 404) toast.error("Conta não encontrada");
    else toast.error("Erro inesperado ao remover comprovante");
  };

  const cpValidateComprovanteFile = (file) => {
    if (!file) return "Selecione um arquivo";
    if (file.size === 0) return "Arquivo vazio";
    if (file.size > CP_COMPROVANTE_MAX_BYTES) {
      return `Arquivo maior que ${CP_COMPROVANTE_MAX_BYTES / 1024 / 1024} MB`;
    }
    const nome = (file.name || "").toLowerCase();
    const extOk = CP_COMPROVANTE_EXT_ALLOWED.some((e) => nome.endsWith(e));
    const mime = (file.type || "").toLowerCase();
    const mimeOk = CP_COMPROVANTE_MIME_ALLOWED.includes(mime);
    if (!extOk && !mimeOk) return "Envie PDF, JPG, JPEG ou PNG";
    return null;
  };

  const cpUploadComprovante = async (file) => {
    if (!detalheAlvo || !podeEscrever) return;
    const erro = cpValidateComprovanteFile(file);
    if (erro) { toast.error(erro); return; }
    setCpUploadingComp(true);
    try {
      const fd = new FormData();
      fd.append("file", file, file.name);
      const { data } = await api.post(
        `/contas-pagar/${detalheAlvo.id}/comprovante`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      // Backend devolve metadados diretamente; atualiza detalhe sem novo GET
      setDetalheAlvo((prev) => prev ? { ...prev,
        comprovante_filename: data.comprovante_filename,
        comprovante_content_type: data.comprovante_content_type,
        comprovante_size: data.comprovante_size,
        comprovante_ext: data.comprovante_ext,
        comprovante_storage_path: data.comprovante_storage_path,
        comprovante_uploaded_at: data.comprovante_uploaded_at,
      } : prev);
      // Refresh silencioso da listagem
      load();
      toast.success("Comprovante anexado");
    } catch (err) {
      _cpCompUploadError(err);
    } finally {
      setCpUploadingComp(false);
    }
  };

  const cpDownloadComprovante = async () => {
    if (!detalheAlvo?.comprovante_storage_path) {
      toast.info("Comprovante não anexado");
      return;
    }
    setCpDownloadingComp(true);
    try {
      const resp = await api.get(
        `/contas-pagar/${detalheAlvo.id}/comprovante`,
        { responseType: "blob" }
      );
      const disp = resp.headers?.["content-disposition"] || resp.headers?.["Content-Disposition"];
      const fallback = detalheAlvo.comprovante_filename
        || `comprovante_${detalheAlvo.id.slice(0, 8)}.${detalheAlvo.comprovante_ext || "bin"}`;
      const filename = cpParseFilenameFromDisposition(disp) || fallback;
      cpSaveBlob(resp.data, filename);
    } catch (err) {
      _cpCompDownloadError(err);
    } finally {
      setCpDownloadingComp(false);
    }
  };

  const cpRemoveComprovante = async () => {
    if (!detalheAlvo || !podeEscrever) return;
    setCpRemovingComp(true);
    try {
      await api.delete(`/contas-pagar/${detalheAlvo.id}/comprovante`);
      setDetalheAlvo((prev) => prev ? { ...prev,
        comprovante_filename: null,
        comprovante_content_type: null,
        comprovante_size: null,
        comprovante_ext: null,
        comprovante_storage_path: null,
        comprovante_uploaded_at: null,
      } : prev);
      load();
      setCpConfirmRemoveOpen(false);
      toast.success("Comprovante removido");
    } catch (err) {
      _cpCompRemoveError(err);
    } finally {
      setCpRemovingComp(false);
    }
  };

  // F13.5.3 — Import CSV
  const csvResetModal = () => {
    setCsvFile(null);
    setCsvResult(null);
    setCsvErro("");
  };

  const csvCloseModal = () => {
    if (csvImporting) return;
    setCsvOpen(false);
    csvResetModal();
  };

  const csvValidateFile = (file) => {
    if (!file) return "Selecione um arquivo CSV";
    if (file.size === 0) return "Arquivo vazio";
    if (file.size > CP_CSV_MAX_BYTES) {
      return `Arquivo maior que ${CP_CSV_MAX_BYTES / 1024 / 1024} MB`;
    }
    const nome = (file.name || "").toLowerCase();
    if (!nome.endsWith(".csv")) return "Extensão inválida — envie um arquivo .csv";
    return null;
  };

  const csvOnPickFile = (file) => {
    setCsvResult(null);
    setCsvErro("");
    if (!file) { setCsvFile(null); return; }
    const erro = csvValidateFile(file);
    if (erro) { toast.error(erro); setCsvFile(null); return; }
    setCsvFile(file);
  };

  const csvBaixarTemplate = async () => {
    setCsvDownloadingTemplate(true);
    try {
      const resp = await api.get("/contas-pagar/csv-template", { responseType: "blob" });
      const disp = resp.headers?.["content-disposition"] || resp.headers?.["Content-Disposition"];
      const filename = cpParseFilenameFromDisposition(disp) || "contas_pagar_template.csv";
      cpSaveBlob(resp.data, filename);
    } catch (err) {
      const s = err?.response?.status;
      if (s === 401) toast.error("Sessão expirada. Faça login novamente.");
      else if (s === 403) toast.error("Sem permissão para baixar o modelo");
      else toast.error("Erro ao baixar o modelo CSV");
    } finally {
      setCsvDownloadingTemplate(false);
    }
  };

  const csvImportar = async () => {
    if (!csvFile || csvImporting) return;
    const erro = csvValidateFile(csvFile);
    if (erro) { toast.error(erro); return; }
    setCsvImporting(true);
    setCsvErro("");
    setCsvResult(null);
    try {
      const fd = new FormData();
      fd.append("file", csvFile, csvFile.name);
      const { data } = await api.post("/contas-pagar/import-csv", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setCsvResult(data || null);
      const imp = Number(data?.imported_count || 0);
      if (imp > 0) {
        toast.success(`${imp} conta${imp === 1 ? "" : "s"} importada${imp === 1 ? "" : "s"}`);
        load();
      } else {
        toast.info("Nenhuma conta importada");
      }
    } catch (err) {
      const s = err?.response?.status;
      const raw = err?.response?.data?.detail;
      const detail = typeof raw === "string" ? raw : (raw ? JSON.stringify(raw) : "");
      if (s === 400) setCsvErro(detail || "Requisição inválida — verifique o cabeçalho do CSV.");
      else if (s === 401) setCsvErro("Sessão expirada. Faça login novamente.");
      else if (s === 403) setCsvErro("Sem permissão para importar CSV.");
      else if (s === 413) setCsvErro(`Arquivo maior que ${CP_CSV_MAX_BYTES / 1024 / 1024} MB`);
      else if (s === 422) setCsvErro(detail || "Payload inválido — verifique o arquivo.");
      else if (s === 500) setCsvErro("Erro interno do servidor ao processar o CSV.");
      else setCsvErro("Erro inesperado ao importar CSV.");
    } finally {
      setCsvImporting(false);
    }
  };

  // F13.5.5 — Export CSV: envia os filtros atualmente ativos ("all" e "" são omitidos)
  const csvExportar = async () => {
    if (csvExporting) return;
    setCsvExporting(true);
    try {
      const params = {};
      if (filtros.data_inicio) params.data_inicio = filtros.data_inicio;
      if (filtros.data_fim) params.data_fim = filtros.data_fim;
      if (filtros.status && filtros.status !== "all") params.status = filtros.status;
      if (filtros.categoria && filtros.categoria !== "all") params.categoria = filtros.categoria;
      if (filtros.pousada_id && filtros.pousada_id !== "all") params.pousada_id = filtros.pousada_id;

      const resp = await api.get("/contas-pagar/export-csv", { params, responseType: "blob" });
      const disp = resp.headers?.["content-disposition"] || resp.headers?.["Content-Disposition"];
      const filename = cpParseFilenameFromDisposition(disp) || "contas_pagar.csv";
      cpSaveBlob(resp.data, filename);
      toast.success("CSV exportado com sucesso.");
    } catch (err) {
      const s = err?.response?.status;
      if (s === 401) toast.error("Sessão expirada. Faça login novamente.");
      else if (s === 403) toast.error("Sem permissão para exportar CSV");
      else if (s === 500) toast.error("Erro interno do servidor ao exportar CSV");
      else if (err?.message?.includes?.("Network") || err?.code === "ERR_NETWORK") {
        toast.error("Falha de conexão — tente novamente");
      } else toast.error("Erro inesperado ao exportar CSV");
    } finally {
      setCsvExporting(false);
    }
  };

  const load = useCallback(async () => {
    setLoading(true); setErroCarga(null);
    try {
      const params = {};
      if (filtros.data_inicio) params.data_inicio = filtros.data_inicio;
      if (filtros.data_fim) params.data_fim = filtros.data_fim;
      if (filtros.status !== "all") params.status = filtros.status;
      if (filtros.categoria !== "all") params.categoria = filtros.categoria;
      if (filtros.pousada_id !== "all") params.pousada_id = filtros.pousada_id;
      const [list, res] = await Promise.all([
        api.get("/contas-pagar", { params }),
        api.get("/contas-pagar/resumo", {
          params: {
            pousada_id: filtros.pousada_id !== "all" ? filtros.pousada_id : undefined,
            data_inicio: filtros.data_inicio,
            data_fim: filtros.data_fim,
          },
        }).catch(() => ({ data: null })),
      ]);
      setItems(Array.isArray(list.data) ? list.data : []);
      setResumo(res.data || null);
    } catch (err) {
      setItems([]); setResumo(null);
      setErroCarga(err?.response?.status === 401
        ? "Sessão expirada. Faça login novamente."
        : "Não foi possível carregar as contas a pagar.");
    } finally {
      setLoading(false);
    }
  }, [filtros]);

  const loadRefs = useCallback(async () => {
    try {
      const [po, u] = await Promise.all([
        api.get("/pousadas-list").catch(() => ({ data: [] })),
        api.get("/auth/me").catch(() => ({ data: null })),
      ]);
      setPousadas(Array.isArray(po.data) ? po.data.filter((x) => x.id) : []);
      setMe(u.data);
    } catch { /* silencioso */ }
  }, []);

  useEffect(() => { loadRefs(); }, [loadRefs]);
  useEffect(() => { load(); }, [load]);

  const pousadaMap = useMemo(() => Object.fromEntries(pousadas.map((p) => [p.id, p.nome])), [pousadas]);

  const itensFiltrados = useMemo(() => {
    const q = busca.trim().toLowerCase();
    if (!q) return items;
    return items.filter((c) => {
      const alvo = [c.descricao, c.fornecedor, c.observacao].filter(Boolean).join(" ").toLowerCase();
      return alvo.includes(q);
    });
  }, [items, busca]);

  const fornecedoresDistintos = useMemo(() => {
    const s = new Set();
    items.forEach((c) => { if (c.fornecedor) s.add(c.fornecedor); });
    return Array.from(s).sort();
  }, [items]);

  const abrirNovo = () => {
    setEditingId(null); setForm(emptyForm()); setFormOpen(true);
  };

  const abrirEdicao = (c) => {
    if (c.status === "cancelada") return toast.error("Conta cancelada não pode ser editada");
    setEditingId(c.id);
    setForm({
      fornecedor: c.fornecedor || "",
      fornecedor_id: c.fornecedor_id || "",
      descricao: c.descricao || "",
      categoria: c.categoria || "outros",
      valor: String(c.valor ?? ""),
      data_vencimento: c.data_vencimento || today(),
      data_programada_pagamento: c.data_programada_pagamento || "",
      forma_pagamento: c.forma_pagamento || "",
      observacao: c.observacao || "",
      competencia: c.competencia || "",
      pousada_id: c.pousada_id || "",
      is_recorrente: !!c.is_recorrente,
      recorrencia_dia_vencimento: c.recorrencia_dia_vencimento ? String(c.recorrencia_dia_vencimento) : "",
      recorrencia_meses_restantes: c.recorrencia_meses_restantes != null ? String(c.recorrencia_meses_restantes) : "",
    });
    setFormOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.fornecedor.trim()) return toast.error("Informe o fornecedor");
    if (!form.categoria) return toast.error("Selecione a categoria");
    const v = Number(form.valor);
    if (!v || v <= 0) return toast.error("Valor deve ser maior que zero");
    if (!form.data_vencimento) return toast.error("Informe a data de vencimento");
    if (form.is_recorrente) {
      const dia = Number(form.recorrencia_dia_vencimento);
      if (!dia || dia < 1 || dia > 31) return toast.error("Dia de recorrência entre 1 e 31");
    }

    setSaving(true);
    try {
      const base = {
        fornecedor: form.fornecedor.trim(),
        fornecedor_id: form.fornecedor_id || null,
        descricao: (form.descricao || "").trim(),
        categoria: form.categoria,
        valor: v,
        data_vencimento: form.data_vencimento,
        data_programada_pagamento: form.data_programada_pagamento || null,
        forma_pagamento: form.forma_pagamento || null,
        observacao: (form.observacao || "").trim(),
        competencia: (form.competencia || "").trim() || null,
        pousada_id: form.pousada_id || null,
        is_recorrente: !!form.is_recorrente,
        recorrencia_dia_vencimento: form.is_recorrente ? Number(form.recorrencia_dia_vencimento) : null,
        recorrencia_meses_restantes:
          form.is_recorrente && form.recorrencia_meses_restantes !== ""
            ? Number(form.recorrencia_meses_restantes) : null,
      };
      if (editingId) {
        await api.patch(`/contas-pagar/${editingId}`, base);
        toast.success("Conta a pagar atualizada");
      } else {
        await api.post("/contas-pagar", base);
        toast.success("Conta a pagar cadastrada");
      }
      setFormOpen(false); setEditingId(null); setForm(emptyForm());
      load();
    } catch (err) {
      const msg = err?.response?.data?.detail || "Erro ao salvar. Verifique os dados.";
      toast.error(String(msg));
    } finally {
      setSaving(false);
    }
  };

  const abrirDetalhe = async (c) => {
    setDetalheAlvo(c); setDetalheOpen(true); setDetalheLoading(true);
    // GET individual não existe explicitamente; usamos o próprio item da listagem
    // (backend não expõe /contas-pagar/{id} pontual)
    setDetalheLoading(false);
  };

  // F13.5.6 — Auditoria CP (carrega ao abrir/trocar o detalhe)
  const carregarCpAuditoria = useCallback(async (contaId) => {
    if (!contaId) { setCpAuditItems([]); setCpAuditErro(""); return; }
    setCpAuditLoading(true);
    setCpAuditErro("");
    try {
      const { data } = await api.get("/contas-pagar/auditoria", {
        params: { conta_pagar_id: contaId, limit: 100 },
      });
      setCpAuditItems(Array.isArray(data) ? data : []);
    } catch (err) {
      const s = err?.response?.status;
      setCpAuditItems([]);
      if (s === 401) setCpAuditErro("Sessão expirada.");
      else if (s === 403) setCpAuditErro("Sem permissão para consultar a auditoria.");
      else setCpAuditErro("Não foi possível carregar a auditoria.");
    } finally {
      setCpAuditLoading(false);
    }
  }, []);

  useEffect(() => {
    if (detalheOpen && detalheAlvo?.id) carregarCpAuditoria(detalheAlvo.id);
    else if (!detalheOpen) { setCpAuditItems([]); setCpAuditErro(""); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detalheOpen, detalheAlvo?.id]);

  const abrirPagar = (c) => {
    if (c.status === "paga") return toast.error("Conta já paga");
    if (c.status === "cancelada") return toast.error("Conta cancelada não pode ser paga");
    setPagarAlvo(c);
    setPagarForm({ data_pagamento: today(), forma_pagamento: c.forma_pagamento || "" });
  };

  const executarPagar = async () => {
    if (!pagarAlvo) return;
    if (!pagarForm.data_pagamento) return toast.error("Informe a data de pagamento");
    setPagando(true);
    try {
      const body = { data_pagamento: pagarForm.data_pagamento };
      if (pagarForm.forma_pagamento) body.forma_pagamento = pagarForm.forma_pagamento;
      await api.post(`/contas-pagar/${pagarAlvo.id}/pagar`, body);
      toast.success("Conta paga com sucesso");
      setPagarAlvo(null);
      load();
    } catch (err) {
      const msg = err?.response?.data?.detail || "Erro ao registrar pagamento";
      toast.error(String(msg));
    } finally {
      setPagando(false);
    }
  };

  const abrirCancelar = (c) => {
    if (c.status === "cancelada") return toast.error("Conta já cancelada");
    if (c.status === "paga") return toast.error("Conta já paga não pode ser cancelada");
    setCancelarAlvo(c); setCancelarMotivo("");
  };

  const executarCancelar = async () => {
    if (!cancelarAlvo) return;
    if (!cancelarMotivo.trim()) return toast.error("Informe o motivo do cancelamento");
    setCancelando(true);
    try {
      const carimbo = `\n\n[CANCELADA em ${today()}] Motivo: ${cancelarMotivo.trim()}`;
      const novaObs = (cancelarAlvo.observacao || "") + carimbo;
      await api.patch(`/contas-pagar/${cancelarAlvo.id}`, {
        status: "cancelada",
        observacao: novaObs,
      });
      toast.success("Conta a pagar cancelada");
      setCancelarAlvo(null); setCancelarMotivo("");
      load();
    } catch (err) {
      const msg = err?.response?.data?.detail || "Erro ao cancelar conta";
      toast.error(String(msg));
    } finally {
      setCancelando(false);
    }
  };

  return (
    <div data-testid="page-contas-pagar">
      <PageHeader
        eyebrow="Financeiro · Contas a Pagar"
        title="Contas a Pagar"
        description="Obrigações financeiras a pagar (F5). Não cria movimentos financeiros nem pagamentos de reserva."
        actions={
          podeEscrever ? (
            <div className="flex flex-wrap items-center gap-2">
              <Button variant="outline" onClick={csvExportar} disabled={csvExporting}
                      data-testid="btn-cp-export-csv">
                <Download size={16} />
                {csvExporting ? "Exportando..." : "Exportar CSV"}
              </Button>
              <Button variant="outline" onClick={() => { csvResetModal(); setCsvOpen(true); }}
                      data-testid="btn-cp-import-csv-abrir">
                <FileSpreadsheet size={16} /> Importar CSV
              </Button>
              <Button variant="outline" onClick={() => { setOcrOpen(true); setOcrStage("upload"); setOcrFile(null); setOcrPreview(null); }}
                      data-testid="btn-cp-import-ocr-abrir">
                <Sparkles size={16} /> Importar conta (Resende IA)
              </Button>
              <Dialog open={formOpen} onOpenChange={(v) => { setFormOpen(v); if (!v) { setEditingId(null); setForm(emptyForm()); } }}>
                <DialogTrigger asChild>
                  <Button data-testid="btn-nova-cp" onClick={abrirNovo}>
                    <Plus size={16} /> Nova conta a pagar
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>{editingId ? "Editar conta a pagar" : "Nova conta a pagar"}</DialogTitle>
                  <DialogDescription>
                    Preencha os dados conforme o contrato oficial F5. Não gera pagamento nem movimento financeiro automaticamente.
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <FornecedorPicker
                      value={form.fornecedor_id}
                      valueTexto={form.fornecedor}
                      onSelect={(f) => setForm((prev) => ({
                        ...prev,
                        fornecedor_id: f?.id || "",
                        fornecedor: f?.razao_social_nome || prev.fornecedor,
                      }))}
                      onClear={() => setForm((prev) => ({ ...prev, fornecedor_id: "" }))}
                      onChangeTexto={(txt) => setForm((prev) => ({ ...prev, fornecedor: txt }))}
                    />
                  </div>
                  <div>
                    <Label htmlFor="cp-categoria">Categoria *</Label>
                    <Select value={form.categoria} onValueChange={(v) => setForm({ ...form, categoria: v })}>
                      <SelectTrigger id="cp-categoria" data-testid="select-cp-categoria"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {CATEGORIAS.map((c) => <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="cp-valor">Valor (BRL) *</Label>
                    <Input id="cp-valor" data-testid="input-cp-valor" type="number" step="0.01" min="0.01"
                      value={form.valor} required
                      onChange={(e) => setForm({ ...form, valor: e.target.value })} />
                  </div>
                  <div>
                    <Label htmlFor="cp-venc">Vencimento *</Label>
                    <Input id="cp-venc" data-testid="input-cp-venc" type="date"
                      value={form.data_vencimento} required
                      onChange={(e) => setForm({ ...form, data_vencimento: e.target.value })} />
                  </div>
                  <div>
                    <Label htmlFor="cp-prog">Data programada de pagamento</Label>
                    <Input id="cp-prog" data-testid="input-cp-prog" type="date"
                      value={form.data_programada_pagamento}
                      onChange={(e) => setForm({ ...form, data_programada_pagamento: e.target.value })} />
                  </div>
                  <div>
                    <Label htmlFor="cp-forma">Forma de pagamento</Label>
                    <Select value={form.forma_pagamento || "none"}
                      onValueChange={(v) => setForm({ ...form, forma_pagamento: v === "none" ? "" : v })}>
                      <SelectTrigger id="cp-forma" data-testid="select-cp-forma"><SelectValue placeholder="—" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">— Não informado</SelectItem>
                        {FORMAS.map((f) => <SelectItem key={f.key} value={f.key}>{f.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="cp-pousada">Pousada</Label>
                    <Select value={form.pousada_id || "none"}
                      onValueChange={(v) => setForm({ ...form, pousada_id: v === "none" ? "" : v })}>
                      <SelectTrigger id="cp-pousada" data-testid="select-cp-pousada">
                        <SelectValue placeholder="Corporativo" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Sem pousada / Corporativo</SelectItem>
                        {pousadas.map((p) => <SelectItem key={p.id} value={p.id}>{p.nome}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="cp-competencia">Competência (opcional)</Label>
                    <Input id="cp-competencia" data-testid="input-cp-competencia" value={form.competencia}
                      onChange={(e) => setForm({ ...form, competencia: e.target.value })}
                      placeholder="YYYY-MM ou YYYY-MM-DD" />
                  </div>
                  <div className="md:col-span-2">
                    <Label htmlFor="cp-descricao">Descrição</Label>
                    <Input id="cp-descricao" data-testid="input-cp-descricao" value={form.descricao}
                      onChange={(e) => setForm({ ...form, descricao: e.target.value })} />
                  </div>
                  <div className="md:col-span-2">
                    <Label htmlFor="cp-obs">Observação</Label>
                    <Input id="cp-obs" data-testid="input-cp-obs" value={form.observacao}
                      onChange={(e) => setForm({ ...form, observacao: e.target.value })} />
                  </div>
                  <div className="md:col-span-2 border-t border-stone-200 pt-3">
                    <div className="flex items-center justify-between mb-2">
                      <div>
                        <Label htmlFor="cp-recorrente">Recorrência mensal</Label>
                        <p className="text-[11px] text-stone-500">Ao pagar, o backend gera a próxima ocorrência automaticamente.</p>
                      </div>
                      <Switch id="cp-recorrente" checked={form.is_recorrente} data-testid="switch-cp-recorrente"
                        onCheckedChange={(v) => setForm({ ...form, is_recorrente: v })} />
                    </div>
                    {form.is_recorrente && (
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label htmlFor="cp-rec-dia">Dia do vencimento *</Label>
                          <Input id="cp-rec-dia" data-testid="input-cp-rec-dia" type="number" min="1" max="31"
                            value={form.recorrencia_dia_vencimento}
                            onChange={(e) => setForm({ ...form, recorrencia_dia_vencimento: e.target.value })} />
                        </div>
                        <div>
                          <Label htmlFor="cp-rec-meses">Meses restantes (vazio = sem limite)</Label>
                          <Input id="cp-rec-meses" data-testid="input-cp-rec-meses" type="number" min="0"
                            value={form.recorrencia_meses_restantes}
                            onChange={(e) => setForm({ ...form, recorrencia_meses_restantes: e.target.value })} />
                        </div>
                      </div>
                    )}
                  </div>
                  <DialogFooter className="md:col-span-2">
                    <Button type="button" variant="outline"
                      onClick={() => { setFormOpen(false); setEditingId(null); setForm(emptyForm()); }}>
                      Cancelar
                    </Button>
                    <Button type="submit" data-testid="btn-salvar-cp" disabled={saving}>
                      {saving ? "Salvando..." : editingId ? "Salvar alterações" : "Cadastrar"}
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          </div>
          ) : null
        }
      />

      {/* KPIs oficiais /contas-pagar/resumo */}
      {resumo && (
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-3 mb-4">
          <KpiCard testid="kpi-cp-vencidas" label="Vencidas" tone="danger"
            count={resumo.vencidas?.count || 0} valor={resumo.vencidas?.valor || 0} />
          <KpiCard testid="kpi-cp-hoje" label="Hoje" tone="warn"
            count={resumo.hoje?.count || 0} valor={resumo.hoje?.valor || 0} />
          <KpiCard testid="kpi-cp-amanha" label="Amanhã" tone="warn"
            count={resumo.amanha?.count || 0} valor={resumo.amanha?.valor || 0} />
          <KpiCard testid="kpi-cp-7d" label="Próximos 7 dias" tone="neutral"
            count={resumo.proximos_7?.count || 0} valor={resumo.proximos_7?.valor || 0} />
          <KpiCard testid="kpi-cp-12d" label="Próximos 12 dias" tone="neutral"
            count={resumo.proximos_12?.count || 0} valor={resumo.proximos_12?.valor || 0} />
          <KpiCard testid="kpi-cp-pendente" label="Pendente global" tone="neutral"
            count={resumo.total_pendente?.count || 0} valor={resumo.total_pendente?.valor || 0} />
          <KpiCard testid="kpi-cp-programado" label="Programado" tone="info"
            count={resumo.total_programado?.count || 0} valor={resumo.total_programado?.valor || 0} />
          <KpiCard testid="kpi-cp-pago" label="Pago no período" tone="ok"
            count={resumo.total_pago_periodo?.count || 0} valor={resumo.total_pago_periodo?.valor || 0} />
        </div>
      )}

      {/* Filtros */}
      <div className="flex flex-wrap items-end gap-3 mb-4">
        <div>
          <Label className="text-[11px] uppercase tracking-widest text-stone-500">Venc. de</Label>
          <Input type="date" data-testid="filter-cp-de" value={filtros.data_inicio}
            onChange={(e) => setFiltros({ ...filtros, data_inicio: e.target.value })} className="w-[150px]" />
        </div>
        <div>
          <Label className="text-[11px] uppercase tracking-widest text-stone-500">Até</Label>
          <Input type="date" data-testid="filter-cp-ate" value={filtros.data_fim}
            onChange={(e) => setFiltros({ ...filtros, data_fim: e.target.value })} className="w-[150px]" />
        </div>
        <div>
          <Label className="text-[11px] uppercase tracking-widest text-stone-500">Status</Label>
          <Select value={filtros.status} onValueChange={(v) => setFiltros({ ...filtros, status: v })}>
            <SelectTrigger data-testid="filter-cp-status" className="w-[150px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              {STATUSES.map((s) => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[11px] uppercase tracking-widest text-stone-500">Categoria</Label>
          <Select value={filtros.categoria} onValueChange={(v) => setFiltros({ ...filtros, categoria: v })}>
            <SelectTrigger data-testid="filter-cp-categoria" className="w-[180px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas</SelectItem>
              {CATEGORIAS.map((c) => <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[11px] uppercase tracking-widest text-stone-500">Pousada</Label>
          <Select value={filtros.pousada_id} onValueChange={(v) => setFiltros({ ...filtros, pousada_id: v })}>
            <SelectTrigger data-testid="filter-cp-pousada" className="w-[180px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas</SelectItem>
              {pousadas.map((p) => <SelectItem key={p.id} value={p.id}>{p.nome}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="flex-1 min-w-[200px]">
          <Label className="text-[11px] uppercase tracking-widest text-stone-500">Busca</Label>
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
            <Input value={busca} data-testid="filter-cp-busca"
              onChange={(e) => setBusca(e.target.value)}
              placeholder="Descrição, fornecedor, observação..." className="pl-8" />
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={load} aria-label="Atualizar" data-testid="btn-refresh-cp">
          <RefreshCw size={14} />
        </Button>
      </div>

      {/* Corpo */}
      {loading ? (
        <div data-testid="cp-loading" className="bg-[#FFFFFF] border border-stone-200 rounded-lg p-10 text-center text-stone-500">
          Carregando contas a pagar...
        </div>
      ) : erroCarga ? (
        <div data-testid="cp-erro" className="bg-[#FFFFFF] border border-[#D4A652]/30 rounded-lg p-10 text-center text-[#D4A652]">
          {erroCarga}
        </div>
      ) : itensFiltrados.length === 0 ? (
        <div data-testid="cp-empty" className="bg-[#FFFFFF] border border-stone-200 rounded-lg p-12 text-center">
          <div className="w-14 h-14 rounded-full bg-[#EAF0EA] flex items-center justify-center mx-auto mb-4 text-[#083A6B]">
            <Receipt size={22} strokeWidth={1.5} />
          </div>
          <div className="font-serif text-2xl text-stone-900 mb-2">Nenhuma conta a pagar encontrada.</div>
          <p className="text-sm text-stone-600 mb-6 max-w-md mx-auto">
            Ajuste os filtros ou cadastre a primeira conta a pagar. Recorrências mensais são geradas automaticamente ao pagar a ocorrência atual.
          </p>
          {podeEscrever && (
            <Button data-testid="btn-empty-nova-cp" onClick={abrirNovo}>
              <Plus size={16} /> Nova conta a pagar
            </Button>
          )}
        </div>
      ) : (
        <>
          {/* Mobile: cards */}
          <div className="md:hidden space-y-3">
            {itensFiltrados.map((c) => {
              const st = c.status_calculado || c.status || "pendente";
              return (
                <div key={c.id} data-testid={`cp-card-${c.id}`}
                  className="bg-[#FFFFFF] border border-stone-200 rounded-lg p-4">
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <div className="min-w-0">
                      <div className="text-xs text-stone-500">Venc. {fmtDate(c.data_vencimento)}</div>
                      <div className="font-serif text-lg text-stone-900 truncate">
                        {c.descricao || c.fornecedor || <span className="text-stone-400">—</span>}
                      </div>
                    </div>
                    <StatusBadge status={st} />
                  </div>
                  <div className="font-serif text-2xl text-stone-900 tabular-nums mb-1">{brl(c.valor)}</div>
                  <div className="text-xs text-stone-600 space-y-0.5 mb-3">
                    <div><b>Fornecedor:</b> {c.fornecedor || "—"}</div>
                    <div><b>Categoria:</b> {CAT_LABEL[c.categoria] || c.categoria}</div>
                    {c.pousada_id && <div><b>Pousada:</b> {c.pousada_nome || pousadaMap[c.pousada_id]}</div>}
                    {c.competencia && <div><b>Competência:</b> {c.competencia}</div>}
                    {c.is_recorrente && (
                      <div className="text-[#5B21B6]">
                        <RotateCw size={11} className="inline" /> Recorrente · dia {c.recorrencia_dia_vencimento}
                      </div>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Button size="sm" variant="outline" onClick={() => abrirDetalhe(c)}
                      data-testid={`btn-cp-detalhe-${c.id}`}><Eye size={14} /> Detalhes</Button>
                    {podeEscrever && st !== "cancelada" && (
                      <>
                        <Button size="sm" variant="outline" onClick={() => abrirEdicao(c)}
                          data-testid={`btn-cp-editar-${c.id}`}><PencilLine size={14} /> Editar</Button>
                        {st !== "paga" && (
                          <Button size="sm" variant="outline" onClick={() => abrirPagar(c)}
                            data-testid={`btn-cp-pagar-${c.id}`}><CheckCircle2 size={14} /> Pagar</Button>
                        )}
                        {st !== "paga" && (
                          <Button size="sm" variant="outline" onClick={() => abrirCancelar(c)}
                            data-testid={`btn-cp-cancelar-${c.id}`}><Ban size={14} /> Cancelar</Button>
                        )}
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Desktop: tabela */}
          <div className="hidden md:block bg-[#FFFFFF] border border-stone-200 rounded-lg overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-stone-50 hover:bg-stone-50">
                  <TableHead className="text-xs uppercase tracking-widest text-stone-500">Vencimento</TableHead>
                  <TableHead className="text-xs uppercase tracking-widest text-stone-500">Descrição</TableHead>
                  <TableHead className="text-xs uppercase tracking-widest text-stone-500">Fornecedor</TableHead>
                  <TableHead className="text-xs uppercase tracking-widest text-stone-500">Categoria</TableHead>
                  <TableHead className="text-xs uppercase tracking-widest text-stone-500">Pousada</TableHead>
                  <TableHead className="text-xs uppercase tracking-widest text-stone-500 text-right">Valor</TableHead>
                  <TableHead className="text-xs uppercase tracking-widest text-stone-500">Competência</TableHead>
                  <TableHead className="text-xs uppercase tracking-widest text-stone-500">Status</TableHead>
                  <TableHead className="text-xs uppercase tracking-widest text-stone-500 text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {itensFiltrados.map((c) => {
                  const st = c.status_calculado || c.status || "pendente";
                  const bloqueada = st === "cancelada";
                  return (
                    <TableRow key={c.id} data-testid={`cp-row-${c.id}`}>
                      <TableCell className="text-stone-700 text-sm whitespace-nowrap">{fmtDate(c.data_vencimento)}</TableCell>
                      <TableCell className="text-stone-700 text-sm max-w-xs truncate">
                        <div className="flex items-center gap-1.5">
                          {c.is_recorrente && (
                            <RotateCw size={12} className="text-[#5B21B6] shrink-0" aria-label="Recorrente" />
                          )}
                          <span className="truncate">{c.descricao || <span className="text-stone-400">—</span>}</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-stone-700 text-sm max-w-[180px] truncate">{c.fornecedor || "—"}</TableCell>
                      <TableCell className="text-stone-700 text-sm">{CAT_LABEL[c.categoria] || c.categoria}</TableCell>
                      <TableCell className="text-stone-700 text-sm">
                        {c.pousada_id
                          ? (c.pousada_nome || pousadaMap[c.pousada_id] || c.pousada_id)
                          : <span className="text-stone-400">Corporativo</span>}
                      </TableCell>
                      <TableCell className="text-right tabular-nums text-stone-900">{brl(c.valor)}</TableCell>
                      <TableCell className="text-stone-700 text-xs">{c.competencia || "—"}</TableCell>
                      <TableCell><StatusBadge status={st} /></TableCell>
                      <TableCell className="text-right">
                        <div className="inline-flex gap-1">
                          <Button size="sm" variant="ghost" onClick={() => abrirDetalhe(c)}
                            aria-label="Ver detalhes" data-testid={`btn-cp-detalhe-${c.id}`}>
                            <Eye size={14} />
                          </Button>
                          {podeEscrever && !bloqueada && (
                            <>
                              <Button size="sm" variant="ghost" onClick={() => abrirEdicao(c)}
                                aria-label="Editar" data-testid={`btn-cp-editar-${c.id}`}>
                                <PencilLine size={14} />
                              </Button>
                              {st !== "paga" && (
                                <Button size="sm" variant="ghost" onClick={() => abrirPagar(c)}
                                  aria-label="Registrar pagamento" data-testid={`btn-cp-pagar-${c.id}`}>
                                  <CheckCircle2 size={14} />
                                </Button>
                              )}
                              {st !== "paga" && (
                                <Button size="sm" variant="ghost" onClick={() => abrirCancelar(c)}
                                  aria-label="Cancelar conta" data-testid={`btn-cp-cancelar-${c.id}`}>
                                  <Ban size={14} />
                                </Button>
                              )}
                            </>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </>
      )}

      {/* Detalhes */}
      <Dialog open={detalheOpen} onOpenChange={(v) => { setDetalheOpen(v); if (!v) setDetalheAlvo(null); }}>
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto" data-testid="dialog-cp-detalhe">
          <DialogHeader>
            <DialogTitle>Detalhes da conta a pagar</DialogTitle>
            <DialogDescription>
              Dados oficiais retornados pelo backend F5. `status_calculado` é derivado (inclui "vencida").
            </DialogDescription>
          </DialogHeader>
          {detalheLoading ? (
            <div className="text-center text-stone-500 py-6">Carregando...</div>
          ) : detalheAlvo ? (
            <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
              <div><div className="text-[11px] uppercase text-stone-500">ID</div><span className="font-mono text-xs break-all">{detalheAlvo.id}</span></div>
              <div><div className="text-[11px] uppercase text-stone-500">Status</div>
                <StatusBadge status={detalheAlvo.status_calculado || detalheAlvo.status} />
                {detalheAlvo.status_calculado && detalheAlvo.status && detalheAlvo.status_calculado !== detalheAlvo.status && (
                  <div className="text-[10px] text-stone-500 mt-1">Base: {STATUS_LABEL[detalheAlvo.status] || detalheAlvo.status}</div>
                )}
              </div>
              <div><div className="text-[11px] uppercase text-stone-500">Fornecedor</div>{detalheAlvo.fornecedor || "—"}
                {detalheAlvo.fornecedor_id && (
                  <div className="text-[10px] text-[#083A6B] mt-0.5" data-testid="cp-detalhe-fornecedor-id">
                    ↳ vínculo estruturado
                  </div>
                )}
              </div>
              <div><div className="text-[11px] uppercase text-stone-500">Categoria</div>{CAT_LABEL[detalheAlvo.categoria] || detalheAlvo.categoria || "—"}</div>
              <div><div className="text-[11px] uppercase text-stone-500">Valor</div><b className="tabular-nums">{brl(detalheAlvo.valor)}</b></div>
              <div><div className="text-[11px] uppercase text-stone-500">Vencimento</div>{fmtDate(detalheAlvo.data_vencimento)}</div>
              <div><div className="text-[11px] uppercase text-stone-500">Programada p/</div>{detalheAlvo.data_programada_pagamento ? fmtDate(detalheAlvo.data_programada_pagamento) : "—"}</div>
              <div><div className="text-[11px] uppercase text-stone-500">Data pagamento</div>{detalheAlvo.data_pagamento ? fmtDate(detalheAlvo.data_pagamento) : "—"}</div>
              <div><div className="text-[11px] uppercase text-stone-500">Forma pagamento</div>{FORMA_LABEL[detalheAlvo.forma_pagamento] || detalheAlvo.forma_pagamento || "—"}</div>
              <div><div className="text-[11px] uppercase text-stone-500">Competência</div>{detalheAlvo.competencia || "—"}</div>
              <div><div className="text-[11px] uppercase text-stone-500">Pousada</div>
                {detalheAlvo.pousada_id
                  ? (detalheAlvo.pousada_nome || pousadaMap[detalheAlvo.pousada_id] || detalheAlvo.pousada_id)
                  : "Corporativo"}
              </div>
              <div><div className="text-[11px] uppercase text-stone-500">Origem</div>
                {detalheAlvo.imported_from_csv ? "Importada de CSV" : "Manual"}
                {detalheAlvo.gerada_automaticamente ? " · Gerada por recorrência" : ""}
              </div>
              <div className="col-span-2"><div className="text-[11px] uppercase text-stone-500">Descrição</div>{detalheAlvo.descricao || "—"}</div>
              <div className="col-span-2"><div className="text-[11px] uppercase text-stone-500">Observação</div>
                <span className="whitespace-pre-wrap">{detalheAlvo.observacao || "—"}</span>
              </div>

              {/* Recorrência */}
              {(detalheAlvo.is_recorrente || detalheAlvo.recorrencia_parent_id) && (
                <div className="col-span-2 border-t border-stone-200 pt-3">
                  <div className="text-[11px] uppercase text-stone-500 mb-1 flex items-center gap-1.5">
                    <RotateCw size={12} /> Recorrência
                  </div>
                  <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-xs">
                    <div><b>Recorrente:</b> {detalheAlvo.is_recorrente ? "Sim" : "Não"}</div>
                    <div><b>Gerada auto.:</b> {detalheAlvo.gerada_automaticamente ? "Sim" : "Não"}</div>
                    <div><b>Dia:</b> {detalheAlvo.recorrencia_dia_vencimento ?? "—"}</div>
                    <div><b>Meses restantes:</b> {detalheAlvo.recorrencia_meses_restantes ?? "sem limite"}</div>
                    {detalheAlvo.recorrencia_parent_id && (
                      <div className="col-span-2"><b>Origem:</b> <span className="font-mono text-[10px] break-all">{detalheAlvo.recorrencia_parent_id}</span></div>
                    )}
                  </div>
                </div>
              )}

              {/* F13.5.2 — Comprovante (upload/download/remoção) */}
              <div className="col-span-2 border-t border-stone-200 pt-3" data-testid="cp-comprovante-section">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-[11px] uppercase text-stone-500 flex items-center gap-1.5">
                    <FileText size={12} /> Comprovante
                  </div>
                  <div className="text-[10px] text-stone-500">
                    Limite: 10 MB · PDF/JPG/PNG
                  </div>
                </div>
                {(() => {
                  const anexado = !!detalheAlvo.comprovante_storage_path;
                  const uploadDisabled = !podeEscrever || cpUploadingComp;
                  const removeDisabled = !podeEscrever || cpRemovingComp;
                  return (
                    <div className="border border-stone-200 rounded p-3 bg-white flex flex-col gap-2"
                         data-testid="cp-comprovante-card">
                      <div className="flex items-center justify-between">
                        <span
                          className={`text-[10px] px-2 py-0.5 rounded-full border ${
                            anexado
                              ? "bg-[#EAF0EA] text-[#083A6B] border-[#083A6B]/20"
                              : "bg-stone-100 text-stone-500 border-stone-300"
                          }`}
                          data-testid="cp-comprovante-status"
                        >
                          {anexado ? "Anexado" : "Não anexado"}
                        </span>
                      </div>

                      <div className="text-xs text-stone-700 space-y-0.5 min-h-[3rem]">
                        {anexado ? (
                          <>
                            <div className="flex gap-1">
                              <span className="text-stone-500 shrink-0">Arquivo:</span>
                              <span className="truncate" title={detalheAlvo.comprovante_filename || ""}
                                    data-testid="cp-comprovante-nome">
                                {detalheAlvo.comprovante_filename || "—"}
                              </span>
                            </div>
                            <div className="flex gap-1">
                              <span className="text-stone-500 shrink-0">Tamanho:</span>
                              <span data-testid="cp-comprovante-size">{cpFmtBytes(detalheAlvo.comprovante_size)}</span>
                            </div>
                            <div className="flex gap-1">
                              <span className="text-stone-500 shrink-0">Upload:</span>
                              <span data-testid="cp-comprovante-data">
                                {cpFmtDataHoraBR(detalheAlvo.comprovante_uploaded_at)}
                              </span>
                            </div>
                            {detalheAlvo.comprovante_content_type && (
                              <div className="flex gap-1">
                                <span className="text-stone-500 shrink-0">Tipo:</span>
                                <span className="font-mono text-[10px]">{detalheAlvo.comprovante_content_type}</span>
                              </div>
                            )}
                          </>
                        ) : (
                          <div className="text-stone-500 italic">Nenhum comprovante anexado.</div>
                        )}
                      </div>

                      <input
                        id="cp-comprovante-file-input"
                        type="file"
                        accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/jpg,image/png"
                        className="hidden"
                        data-testid="input-cp-comprovante"
                        onChange={(e) => {
                          const f = e.target.files?.[0];
                          e.target.value = ""; // permite re-enviar mesmo arquivo
                          if (f) cpUploadComprovante(f);
                        }}
                      />
                      <div className="flex flex-wrap gap-2 pt-1">
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={uploadDisabled}
                          onClick={() => document.getElementById("cp-comprovante-file-input")?.click()}
                          data-testid="btn-cp-comprovante-upload"
                          aria-label={anexado ? "Substituir comprovante" : "Enviar comprovante"}
                        >
                          <UploadCloud size={14} className="mr-1" />
                          {cpUploadingComp
                            ? "Enviando..."
                            : anexado ? "Substituir" : "Enviar comprovante"}
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          disabled={!anexado || cpDownloadingComp}
                          onClick={cpDownloadComprovante}
                          data-testid="btn-cp-comprovante-download"
                          aria-label="Baixar comprovante"
                        >
                          <Download size={14} className="mr-1" />
                          {cpDownloadingComp ? "Baixando..." : "Baixar"}
                        </Button>
                        {anexado && (
                          <Button
                            size="sm"
                            variant="ghost"
                            disabled={removeDisabled}
                            onClick={() => setCpConfirmRemoveOpen(true)}
                            data-testid="btn-cp-comprovante-remover"
                            aria-label="Remover comprovante"
                            className="text-[#D4A652] hover:bg-[#F7E4DE]"
                          >
                            <Trash2 size={14} className="mr-1" />
                            Remover
                          </Button>
                        )}
                      </div>
                    </div>
                  );
                })()}
                <p className="text-[10px] text-stone-500 mt-2">
                  Formatos aceitos: PDF, JPG, JPEG e PNG. A remoção mantém o histórico do arquivo no storage (limpa apenas os metadados desta conta).
                </p>
              </div>

              {/* F13.5.6 — Auditoria (somente leitura) */}
              <div className="col-span-2 border-t border-stone-200 pt-3" data-testid="cp-auditoria-section">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-[11px] uppercase tracking-widest text-stone-500">Auditoria</div>
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] text-stone-500">Somente leitura</span>
                    <Button size="sm" variant="ghost" onClick={() => carregarCpAuditoria(detalheAlvo.id)}
                            disabled={cpAuditLoading}
                            data-testid="btn-cp-auditoria-retry"
                            aria-label="Recarregar auditoria">
                      <RefreshCw size={12} />
                    </Button>
                  </div>
                </div>

                {cpAuditLoading ? (
                  <div className="text-xs text-stone-500 py-2" data-testid="cp-auditoria-loading">
                    Carregando histórico...
                  </div>
                ) : cpAuditErro ? (
                  <div className="text-xs rounded border border-[#D4A652]/30 bg-[#F7E4DE] text-[#8A3A22] px-2 py-2 flex items-center justify-between"
                       data-testid="cp-auditoria-erro">
                    <span>{cpAuditErro}</span>
                    <Button size="sm" variant="outline"
                            onClick={() => carregarCpAuditoria(detalheAlvo.id)}
                            data-testid="btn-cp-auditoria-retry-inline">
                      Tentar novamente
                    </Button>
                  </div>
                ) : cpAuditItems.length === 0 ? (
                  <div className="text-xs text-stone-500 italic py-2" data-testid="cp-auditoria-vazio">
                    Nenhum evento de auditoria encontrado.
                  </div>
                ) : (
                  <ol className="space-y-2" data-testid="cp-auditoria-lista">
                    {cpAuditItems.map((ev, idx) => {
                      const labelMap = {
                        criada: { label: "Criação", cls: "bg-[#EAF0EA] text-[#083A6B] border-[#083A6B]/20" },
                        atualizada: { label: "Edição", cls: "bg-blue-50 text-blue-700 border-blue-200" },
                        excluida: { label: "Exclusão", cls: "bg-stone-200 text-stone-600 border-stone-400" },
                        paga: { label: "Pagamento", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
                        importada_csv: { label: "Import CSV", cls: "bg-amber-50 text-amber-700 border-amber-200" },
                        exportada_csv: { label: "Export CSV", cls: "bg-indigo-50 text-indigo-700 border-indigo-200" },
                        comprovante_adicionado: { label: "Comprovante +", cls: "bg-[#EAF0EA] text-[#083A6B] border-[#083A6B]/20" },
                        comprovante_removido: { label: "Comprovante −", cls: "bg-orange-50 text-orange-700 border-orange-200" },
                      };
                      const meta = labelMap[ev.acao] || { label: ev.acao || "—", cls: "bg-stone-100 text-stone-600 border-stone-300" };
                      const ex = ev.extras || {};
                      let resumo = "";
                      switch (ev.acao) {
                        case "criada": resumo = "Conta a pagar criada."; break;
                        case "atualizada": {
                          const campos = Array.isArray(ex.campos_alterados) ? ex.campos_alterados : [];
                          resumo = campos.length ? `Campos alterados: ${campos.join(", ")}` : "Conta editada.";
                          break;
                        }
                        case "excluida": resumo = "Conta removida."; break;
                        case "paga": {
                          const val = typeof ex.valor === "number" ? cpFmtBytes(0).replace(/.*/, "") + brl(ex.valor) : "";
                          resumo = `Pagamento em ${fmtDate(ex.data_pagamento) || "—"}${ex.forma_pagamento ? ` · ${ex.forma_pagamento}` : ""}${val ? ` · ${val}` : ""}`;
                          break;
                        }
                        case "importada_csv": resumo = `Importada via CSV${ex.linha_csv ? ` (linha ${ex.linha_csv})` : ""}.`; break;
                        case "exportada_csv": resumo = `Exportação CSV — ${ex.linhas_exportadas ?? 0} linha(s).`; break;
                        case "comprovante_adicionado": resumo = `Anexo: ${ex.filename || "—"}${ex.size ? ` (${cpFmtBytes(ex.size)})` : ""}`; break;
                        case "comprovante_removido": resumo = `Anexo removido${ex.filename ? `: ${ex.filename}` : ""}.`; break;
                        default: resumo = "";
                      }
                      const temDetalhes = !!(ev.extras && Object.keys(ev.extras).length) ||
                                          !!(ev.before && Object.keys(ev.before || {}).length) ||
                                          !!(ev.after && Object.keys(ev.after || {}).length);
                      return (
                        <li key={ev.id || idx}
                            className="border border-stone-200 rounded p-2 bg-white"
                            data-testid={`cp-auditoria-item-${idx}`}>
                          <div className="flex flex-wrap items-center gap-x-2 gap-y-1 mb-1">
                            <span
                              className={`inline-block px-2 py-0.5 rounded-full text-[10px] uppercase tracking-widest font-semibold border ${meta.cls}`}
                              data-testid={`cp-auditoria-acao-${idx}`}>
                              {meta.label}
                            </span>
                            <span className="text-[11px] text-stone-500 tabular-nums"
                                  data-testid={`cp-auditoria-quando-${idx}`}>
                              {cpFmtDataHoraBR(ev.quando)}
                            </span>
                            <span className="text-[11px] text-stone-500 truncate"
                                  title={ev.por || ""}
                                  data-testid={`cp-auditoria-por-${idx}`}>
                              · {ev.por || "—"}
                            </span>
                          </div>
                          {resumo && (
                            <div className="text-xs text-stone-800 break-words"
                                 data-testid={`cp-auditoria-resumo-${idx}`}>
                              {resumo}
                            </div>
                          )}
                          {temDetalhes && (
                            <details className="mt-1" data-testid={`cp-auditoria-detalhes-${idx}`}>
                              <summary className="text-[10px] text-stone-500 cursor-pointer select-none">
                                Ver detalhes técnicos
                              </summary>
                              <pre className="mt-1 text-[10px] leading-tight bg-stone-50 border border-stone-200 rounded p-2 overflow-x-auto whitespace-pre-wrap break-words">
{JSON.stringify(
  {
    ...(ev.extras && Object.keys(ev.extras).length ? { extras: ev.extras } : {}),
    ...(ev.before ? { before: ev.before } : {}),
    ...(ev.after ? { after: ev.after } : {}),
  },
  null,
  2,
)}
                              </pre>
                            </details>
                          )}
                        </li>
                      );
                    })}
                  </ol>
                )}
                <p className="text-[10px] text-stone-500 mt-2">
                  Histórico append-only mantido pelo backend. Auditoria é somente leitura — nenhum evento pode ser editado ou removido pela UI.
                </p>
              </div>

              <div className="col-span-2 text-[11px] text-stone-500 pt-2 border-t border-stone-200">
                Criada em {detalheAlvo.created_at?.slice(0, 10) || "—"} · Atualizada em {detalheAlvo.updated_at?.slice(0, 10) || "—"}
              </div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>

      {/* Pagar */}
      <Dialog open={!!pagarAlvo} onOpenChange={(v) => { if (!v) setPagarAlvo(null); }}>
        <DialogContent className="max-w-md" data-testid="dialog-cp-pagar">
          <DialogHeader>
            <DialogTitle>Registrar pagamento</DialogTitle>
            <DialogDescription>
              Marca a conta como paga (F5). Se for recorrente, a próxima ocorrência é gerada automaticamente pelo backend. Não gera pagamento de reserva, nem movimento financeiro (F6).
            </DialogDescription>
          </DialogHeader>
          {pagarAlvo && (
            <div className="text-sm text-stone-700 space-y-1">
              <div className="text-xs text-stone-500 truncate">{pagarAlvo.descricao || pagarAlvo.fornecedor || "—"}</div>
              <div>Valor: <b className="tabular-nums">{brl(pagarAlvo.valor)}</b> · Venc.: {fmtDate(pagarAlvo.data_vencimento)}</div>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="cp-pag-data">Data de pagamento *</Label>
              <Input id="cp-pag-data" data-testid="input-cp-pag-data" type="date"
                value={pagarForm.data_pagamento}
                onChange={(e) => setPagarForm({ ...pagarForm, data_pagamento: e.target.value })} />
            </div>
            <div>
              <Label htmlFor="cp-pag-forma">Forma de pagamento</Label>
              <Select value={pagarForm.forma_pagamento || "none"}
                onValueChange={(v) => setPagarForm({ ...pagarForm, forma_pagamento: v === "none" ? "" : v })}>
                <SelectTrigger id="cp-pag-forma" data-testid="select-cp-pag-forma"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— Não informado</SelectItem>
                  {FORMAS.map((f) => <SelectItem key={f.key} value={f.key}>{f.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPagarAlvo(null)} disabled={pagando}>Cancelar</Button>
            <Button data-testid="btn-confirmar-pagar" onClick={executarPagar} disabled={pagando}>
              {pagando ? "Registrando..." : "Confirmar pagamento"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancelar (PATCH status=cancelada) */}
      <Dialog open={!!cancelarAlvo} onOpenChange={(v) => { if (!v) { setCancelarAlvo(null); setCancelarMotivo(""); } }}>
        <DialogContent className="max-w-md" data-testid="dialog-cp-cancelar">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle size={18} className="text-[#D4A652]" /> Cancelar conta a pagar?
            </DialogTitle>
            <DialogDescription>
              A conta será marcada como cancelada. Não usa DELETE — o registro é preservado. O motivo é adicionado às observações para auditoria.
            </DialogDescription>
          </DialogHeader>
          {cancelarAlvo && (
            <div className="text-sm text-stone-700 space-y-1">
              <div className="text-xs text-stone-500 truncate">{cancelarAlvo.descricao || cancelarAlvo.fornecedor || "—"}</div>
              <div>Valor: <b className="tabular-nums">{brl(cancelarAlvo.valor)}</b> · Venc.: {fmtDate(cancelarAlvo.data_vencimento)}</div>
            </div>
          )}
          <div>
            <Label htmlFor="cp-cancel-motivo">Motivo *</Label>
            <Input id="cp-cancel-motivo" data-testid="input-cp-cancel-motivo"
              value={cancelarMotivo} onChange={(e) => setCancelarMotivo(e.target.value)}
              placeholder="Descreva o motivo do cancelamento" />
            <p className="text-[11px] text-stone-500 mt-1">
              O motivo será acrescentado à observação existente (append), sem sobrescrever.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setCancelarAlvo(null); setCancelarMotivo(""); }} disabled={cancelando}>
              Voltar
            </Button>
            <Button data-testid="btn-confirmar-cancelar-cp" onClick={executarCancelar}
              disabled={cancelando || !cancelarMotivo.trim()}>
              {cancelando ? "Cancelando..." : "Confirmar cancelamento"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* F13.5.2 — Confirmação de remoção de comprovante */}
      <Dialog open={cpConfirmRemoveOpen} onOpenChange={(v) => { if (!v && !cpRemovingComp) setCpConfirmRemoveOpen(false); }}>
        <DialogContent className="max-w-md" data-testid="dialog-cp-comprovante-remover">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle size={18} className="text-[#D4A652]" /> Remover comprovante?
            </DialogTitle>
            <DialogDescription>
              Os metadados do comprovante serão apagados desta conta. O arquivo permanece armazenado no Object Storage (limitação do provedor) — mas deixa de ficar vinculado a este registro.
            </DialogDescription>
          </DialogHeader>
          {detalheAlvo?.comprovante_filename && (
            <div className="text-sm text-stone-700 border border-stone-200 rounded p-2 bg-stone-50">
              <div className="truncate"><b>Arquivo:</b> {detalheAlvo.comprovante_filename}</div>
              <div className="text-xs text-stone-500">
                {cpFmtBytes(detalheAlvo.comprovante_size)} · Enviado em {cpFmtDataHoraBR(detalheAlvo.comprovante_uploaded_at)}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setCpConfirmRemoveOpen(false)} disabled={cpRemovingComp}
                    data-testid="btn-cp-comprovante-remover-cancelar">
              Voltar
            </Button>
            <Button onClick={cpRemoveComprovante} disabled={cpRemovingComp}
                    data-testid="btn-cp-comprovante-remover-confirmar"
                    className="bg-[#D4A652] hover:bg-[#a04e33] text-white">
              {cpRemovingComp ? "Removendo..." : "Confirmar remoção"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* F13.5.3 — Modal de Importação CSV */}
      <Dialog open={csvOpen} onOpenChange={(v) => { if (!v) csvCloseModal(); else setCsvOpen(true); }}>
        <DialogContent className="max-w-[95vw] md:max-w-2xl max-h-[90vh] overflow-y-auto"
                        style={{ maxWidth: 'min(95vw, 42rem)' }}
                        data-testid="dialog-cp-import-csv">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileSpreadsheet size={18} className="text-[#083A6B]" /> Importar Contas a Pagar via CSV
            </DialogTitle>
            <DialogDescription className="break-words">
              Envie um arquivo CSV com cabeçalhos obrigatórios <b>fornecedor</b>, <b>valor</b>, <b>data_vencimento</b> e <b>categoria</b>. Opcionais: <span className="whitespace-normal">descricao, forma_pagamento, pousada_id, pousada_nome, data_programada_pagamento, observacao</span>. Separador aceito: ponto-e-vírgula ou vírgula. Encoding UTF-8. Limite: 2 MB. Duplicados por (fornecedor + valor + vencimento) são pulados.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <div className="flex flex-wrap items-center gap-2">
              <Button variant="outline" size="sm" onClick={csvBaixarTemplate}
                      disabled={csvDownloadingTemplate}
                      data-testid="btn-cp-import-csv-modelo">
                <Download size={14} className="mr-1" />
                {csvDownloadingTemplate ? "Baixando..." : "Baixar modelo CSV"}
              </Button>
              <span className="text-[11px] text-stone-500">
                Cabeçalho e uma linha exemplo — abre direto no Excel/LibreOffice.
              </span>
            </div>

            <div className="border border-stone-300 rounded-lg p-4 bg-[#FFFFFF]">
              <input
                id="cp-csv-file-input"
                type="file"
                accept=".csv,text/csv,application/csv,application/vnd.ms-excel"
                className="hidden"
                data-testid="input-cp-csv"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  e.target.value = "";
                  csvOnPickFile(f || null);
                }}
              />
              {!csvFile ? (
                <div className="text-center py-4">
                  <FileSpreadsheet size={28} strokeWidth={1.5} className="text-[#083A6B] mx-auto mb-2" />
                  <div className="text-sm text-stone-700 mb-2">Nenhum arquivo selecionado.</div>
                  <Button variant="outline" size="sm"
                          onClick={() => document.getElementById("cp-csv-file-input")?.click()}
                          data-testid="btn-cp-csv-selecionar">
                    Selecionar arquivo CSV
                  </Button>
                  <p className="text-[11px] text-stone-500 mt-2">Aceita apenas <b>.csv</b> · até 2 MB</p>
                </div>
              ) : (
                <div className="flex items-center gap-3">
                  <FileSpreadsheet size={20} className="text-[#083A6B] shrink-0" />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm text-stone-900 font-medium truncate" title={csvFile.name}
                         data-testid="cp-csv-file-nome">
                      {csvFile.name}
                    </div>
                    <div className="text-[11px] text-stone-500" data-testid="cp-csv-file-size">
                      {cpFmtBytes(csvFile.size)}
                    </div>
                  </div>
                  <Button variant="ghost" size="sm"
                          onClick={() => document.getElementById("cp-csv-file-input")?.click()}
                          disabled={csvImporting}
                          data-testid="btn-cp-csv-trocar">
                    Trocar
                  </Button>
                </div>
              )}
            </div>

            {csvErro && (
              <div className="text-xs rounded border border-[#D4A652]/30 bg-[#F7E4DE] text-[#8A3A22] px-2 py-2"
                   data-testid="cp-csv-erro">
                {csvErro}
              </div>
            )}

            {csvResult && (
              <div className="border border-stone-200 rounded-lg overflow-hidden"
                   data-testid="cp-csv-resultado">
                <div className="bg-stone-50 border-b border-stone-200 px-3 py-2 text-[11px] uppercase tracking-widest text-stone-600 flex flex-wrap gap-x-4 gap-y-1">
                  <span>Total: <b data-testid="cp-csv-total">{csvResult.total_linhas ?? 0}</b></span>
                  <span className="text-[#083A6B]">Importadas: <b data-testid="cp-csv-imported">{csvResult.imported_count ?? 0}</b></span>
                  <span className="text-[#D4A652]">Rejeitadas: <b data-testid="cp-csv-rejected">{csvResult.rejected_count ?? 0}</b></span>
                  <span className="text-amber-700">Duplicadas: <b data-testid="cp-csv-duplicates">{csvResult.duplicates_count ?? 0}</b></span>
                </div>

                {Array.isArray(csvResult.imported) && csvResult.imported.length > 0 && (
                  <div className="p-3 border-b border-stone-200"
                       data-testid="cp-csv-imported-list">
                    <div className="text-xs font-semibold text-[#083A6B] mb-1">Importadas ({csvResult.imported.length})</div>
                    <div className="max-h-40 overflow-y-auto text-xs">
                      <table className="w-full">
                        <thead className="text-[10px] uppercase text-stone-500">
                          <tr>
                            <th className="text-left px-1 py-0.5">Linha</th>
                            <th className="text-left px-1 py-0.5">Fornecedor</th>
                            <th className="text-right px-1 py-0.5">Valor</th>
                            <th className="text-left px-1 py-0.5">Venc.</th>
                          </tr>
                        </thead>
                        <tbody>
                          {csvResult.imported.map((it, i) => (
                            <tr key={`imp-${i}`} className="border-t border-stone-100">
                              <td className="px-1 py-0.5">{it.linha}</td>
                              <td className="px-1 py-0.5 truncate max-w-[220px]" title={it.fornecedor}>{it.fornecedor}</td>
                              <td className="px-1 py-0.5 text-right tabular-nums">{brl(it.valor)}</td>
                              <td className="px-1 py-0.5">{fmtDate(it.data_vencimento)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {Array.isArray(csvResult.rejected) && csvResult.rejected.length > 0 && (
                  <div className="p-3 border-b border-stone-200 bg-[#FFFFFF]"
                       data-testid="cp-csv-rejected-list">
                    <div className="text-xs font-semibold text-[#D4A652] mb-1">Rejeitadas ({csvResult.rejected.length})</div>
                    <div className="max-h-40 overflow-y-auto text-xs space-y-1.5">
                      {csvResult.rejected.map((it, i) => (
                        <div key={`rej-${i}`} className="border border-stone-200 rounded p-1.5 bg-white">
                          <div className="flex items-center gap-2">
                            <span className="text-[10px] uppercase text-stone-500">Linha</span>
                            <span className="font-mono text-xs">{it.linha}</span>
                          </div>
                          <div className="mt-0.5">
                            <b className="text-[#D4A652]">Motivos:</b> {(it.motivos || []).join(" · ")}
                          </div>
                          {it.dados && Object.keys(it.dados).length > 0 && (
                            <details className="mt-0.5">
                              <summary className="text-[10px] text-stone-500 cursor-pointer">Dados recebidos</summary>
                              <pre className="text-[10px] bg-stone-50 border border-stone-200 rounded p-1 mt-1 overflow-x-auto whitespace-pre-wrap break-words">{JSON.stringify(it.dados, null, 2)}</pre>
                            </details>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {Array.isArray(csvResult.skipped_duplicates) && csvResult.skipped_duplicates.length > 0 && (
                  <div className="p-3"
                       data-testid="cp-csv-duplicates-list">
                    <div className="text-xs font-semibold text-amber-700 mb-1">Duplicadas — puladas ({csvResult.skipped_duplicates.length})</div>
                    <div className="max-h-40 overflow-y-auto text-xs">
                      <table className="w-full">
                        <thead className="text-[10px] uppercase text-stone-500">
                          <tr>
                            <th className="text-left px-1 py-0.5">Linha</th>
                            <th className="text-left px-1 py-0.5">Conta existente</th>
                            <th className="text-left px-1 py-0.5">Fornecedor</th>
                            <th className="text-right px-1 py-0.5">Valor</th>
                            <th className="text-left px-1 py-0.5">Venc.</th>
                          </tr>
                        </thead>
                        <tbody>
                          {csvResult.skipped_duplicates.map((it, i) => (
                            <tr key={`dup-${i}`} className="border-t border-stone-100">
                              <td className="px-1 py-0.5">{it.linha}</td>
                              <td className="px-1 py-0.5 font-mono text-[10px] truncate max-w-[140px]" title={it.conta_id_existente}>{it.conta_id_existente}</td>
                              <td className="px-1 py-0.5 truncate max-w-[180px]" title={it.fornecedor}>{it.fornecedor}</td>
                              <td className="px-1 py-0.5 text-right tabular-nums">{brl(it.valor)}</td>
                              <td className="px-1 py-0.5">{fmtDate(it.data_vencimento)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {(csvResult.imported_count === 0 && csvResult.rejected_count === 0 && csvResult.duplicates_count === 0) && (
                  <div className="p-3 text-xs text-stone-500 italic" data-testid="cp-csv-vazio">
                    Nenhuma linha processada — verifique o conteúdo do arquivo.
                  </div>
                )}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={csvCloseModal} disabled={csvImporting}
                    data-testid="btn-cp-csv-cancelar">
              {csvResult ? "Fechar" : "Cancelar"}
            </Button>
            <Button onClick={csvImportar}
                    disabled={!csvFile || csvImporting}
                    data-testid="btn-cp-csv-importar">
              <UploadCloud size={14} className="mr-1" />
              {csvImporting ? "Importando..." : "Importar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* F-FIX.CP.9 — Importar conta via Resende IA (OCR + prévia + confirmação). */}
      <Dialog open={ocrOpen} onOpenChange={(v) => {
        setOcrOpen(v);
        if (!v) { setOcrStage("upload"); setOcrFile(null); setOcrPreview(null); setOcrSaving(false); }
      }}>
        <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="dialog-cp-ocr">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles size={18} className="text-[#D4A652]" /> Importar conta — Resende IA
            </DialogTitle>
            <DialogDescription>
              Envie um boleto, nota ou foto da conta (PDF, JPG ou PNG até 10&nbsp;MB).
              A Resende IA extrai os dados para você conferir e confirmar.
              <b className="block mt-1 text-[#083A6B]">Nada é lançado sem sua confirmação.</b>
            </DialogDescription>
          </DialogHeader>

          {ocrStage === "upload" && (
            <div className="py-4 space-y-4" data-testid="cp-ocr-upload-stage">
              <label
                htmlFor="cp-ocr-file-input"
                className="block border-2 border-dashed border-stone-300 rounded-lg p-8 text-center hover:border-[#D4A652] hover:bg-[#FDF8EE] cursor-pointer transition-colors"
              >
                <UploadCloud size={28} className="mx-auto text-stone-400 mb-2" />
                <div className="text-sm font-medium text-stone-700">
                  {ocrFile ? ocrFile.name : "Clique para escolher o arquivo"}
                </div>
                <div className="text-xs text-stone-500 mt-1">
                  PDF, JPG ou PNG · até 10&nbsp;MB
                </div>
                <input
                  id="cp-ocr-file-input"
                  data-testid="input-cp-ocr-file"
                  type="file"
                  accept="application/pdf,image/jpeg,image/jpg,image/png"
                  className="hidden"
                  onChange={(e) => setOcrFile(e.target.files?.[0] || null)}
                />
              </label>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOcrOpen(false)} data-testid="btn-cp-ocr-cancelar">
                  Cancelar
                </Button>
                <Button
                  disabled={!ocrFile}
                  onClick={async () => {
                    if (!ocrFile) return;
                    setOcrStage("processing");
                    const fd = new FormData();
                    fd.append("file", ocrFile);
                    try {
                      const { data } = await api.post("/contas-pagar/ocr-preview", fd, {
                        headers: { "Content-Type": "multipart/form-data" },
                        timeout: 90000,
                      });
                      setOcrPreview({
                        fornecedor: data.fornecedor || "",
                        cnpj_cpf: data.cnpj_cpf || "",
                        numero_documento: data.numero_documento || "",
                        descricao: data.descricao || "",
                        data_emissao: data.data_emissao || "",
                        data_vencimento: data.data_vencimento || today(),
                        valor: data.valor != null ? String(data.valor) : "",
                        categoria: data.categoria || "outros",
                        forma_pagamento: data.forma_pagamento || "",
                        observacao: data.observacoes || "",
                        confidence: data.confidence || {},
                      });
                      setOcrStage("preview");
                    } catch (err) {
                      toast.error(err?.response?.data?.detail || "Resende IA não conseguiu ler o documento");
                      setOcrStage("upload");
                    }
                  }}
                  data-testid="btn-cp-ocr-processar"
                >
                  <Sparkles size={14} className="mr-1" /> Ler com Resende IA
                </Button>
              </DialogFooter>
            </div>
          )}

          {ocrStage === "processing" && (
            <div className="py-16 text-center" data-testid="cp-ocr-processing-stage">
              <Loader2 size={32} className="mx-auto animate-spin text-[#083A6B] mb-3" />
              <div className="text-sm text-stone-700">Resende IA está lendo o documento…</div>
              <div className="text-xs text-stone-500 mt-1">Isso pode levar alguns segundos.</div>
            </div>
          )}

          {ocrStage === "preview" && ocrPreview && (
            <div className="py-4 space-y-4" data-testid="cp-ocr-preview-stage">
              <div className="rounded-md bg-[#FDF8EE] border border-[#D4A652]/30 p-3 text-xs text-[#8B6A1F]">
                <b>Conta identificada.</b> Revise os dados antes de confirmar. Campos marcados em dourado precisam de atenção.
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {[
                  { key: "fornecedor", label: "Fornecedor *", required: true, span2: true },
                  { key: "cnpj_cpf", label: "CNPJ/CPF" },
                  { key: "numero_documento", label: "Número do documento" },
                  { key: "data_emissao", label: "Data de emissão", type: "date" },
                  { key: "data_vencimento", label: "Data de vencimento *", type: "date", required: true },
                  { key: "valor", label: "Valor (BRL) *", type: "number", required: true, step: "0.01" },
                ].map((f) => (
                  <div key={f.key} className={f.span2 ? "md:col-span-2" : ""}>
                    <Label htmlFor={`ocr-${f.key}`}>
                      {f.label}
                      {ocrPreview.confidence?.[f.key] === false && (
                        <span className="ml-1 text-[10px] text-[#8B6A1F]" data-testid={`ocr-low-conf-${f.key}`}>· confira</span>
                      )}
                    </Label>
                    <Input
                      id={`ocr-${f.key}`}
                      data-testid={`input-ocr-${f.key}`}
                      type={f.type || "text"}
                      step={f.step}
                      value={ocrPreview[f.key] || ""}
                      className={ocrPreview.confidence?.[f.key] === false ? "border-[#D4A652]" : ""}
                      onChange={(e) => setOcrPreview((p) => ({ ...p, [f.key]: e.target.value }))}
                    />
                  </div>
                ))}
                <div>
                  <Label htmlFor="ocr-categoria">Categoria *</Label>
                  <Select
                    value={ocrPreview.categoria}
                    onValueChange={(v) => setOcrPreview((p) => ({ ...p, categoria: v }))}
                  >
                    <SelectTrigger id="ocr-categoria" data-testid="select-ocr-categoria"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {CATEGORIAS.map((c) => <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="ocr-forma">Forma de pagamento</Label>
                  <Select
                    value={ocrPreview.forma_pagamento || "none"}
                    onValueChange={(v) => setOcrPreview((p) => ({ ...p, forma_pagamento: v === "none" ? "" : v }))}
                  >
                    <SelectTrigger id="ocr-forma" data-testid="select-ocr-forma"><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">—</SelectItem>
                      {["dinheiro","pix","cartao_debito","cartao_credito","boleto","transferencia","cheque"].map((k) => (
                        <SelectItem key={k} value={k}>{k.replace("_"," ").replace(/^./, (c) => c.toUpperCase())}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="md:col-span-2">
                  <Label htmlFor="ocr-descricao">Descrição</Label>
                  <Input
                    id="ocr-descricao"
                    data-testid="input-ocr-descricao"
                    value={ocrPreview.descricao || ""}
                    onChange={(e) => setOcrPreview((p) => ({ ...p, descricao: e.target.value }))}
                  />
                </div>
                <div className="md:col-span-2">
                  <Label htmlFor="ocr-obs">Observações</Label>
                  <Input
                    id="ocr-obs"
                    data-testid="input-ocr-observacao"
                    value={ocrPreview.observacao || ""}
                    onChange={(e) => setOcrPreview((p) => ({ ...p, observacao: e.target.value }))}
                  />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOcrStage("upload")} data-testid="btn-ocr-voltar">
                  Voltar
                </Button>
                <Button
                  disabled={ocrSaving}
                  onClick={async () => {
                    if (!ocrPreview.fornecedor?.trim()) return toast.error("Informe o fornecedor");
                    const v = Number(ocrPreview.valor);
                    if (!v || v <= 0) return toast.error("Valor deve ser maior que zero");
                    if (!ocrPreview.data_vencimento) return toast.error("Informe o vencimento");
                    setOcrSaving(true);
                    try {
                      await api.post("/contas-pagar", {
                        fornecedor: ocrPreview.fornecedor.trim(),
                        descricao: (ocrPreview.descricao || "").trim(),
                        categoria: ocrPreview.categoria,
                        valor: v,
                        data_vencimento: ocrPreview.data_vencimento,
                        forma_pagamento: ocrPreview.forma_pagamento || null,
                        observacao: [
                          ocrPreview.observacao || "",
                          ocrPreview.cnpj_cpf ? `CNPJ/CPF: ${ocrPreview.cnpj_cpf}` : "",
                          ocrPreview.numero_documento ? `Doc: ${ocrPreview.numero_documento}` : "",
                          ocrPreview.data_emissao ? `Emissão: ${ocrPreview.data_emissao}` : "",
                          "(Importado via Resende IA)",
                        ].filter(Boolean).join(" · "),
                      });
                      toast.success("Conta criada a partir da leitura da Resende IA");
                      setOcrOpen(false);
                      load();
                    } catch (err) {
                      toast.error(err?.response?.data?.detail || "Erro ao criar conta");
                    } finally {
                      setOcrSaving(false);
                    }
                  }}
                  data-testid="btn-ocr-confirmar-lancar"
                >
                  {ocrSaving ? "Lançando..." : "Confirmar e lançar"}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
